import React, { useState, useEffect } from 'react';
import { 
  Layers, 
  Plus, 
  Trash2, 
  Edit3, 
  Save, 
  Clock, 
  Upload, 
  Eye, 
  X, 
  Image as ImageIcon,
  Loader2
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const getAuthHeaders = () => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function AdmissionsLevelsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({ 
    title: '', 
    subtitle: '', 
    description: '', 
    levels: [] 
  });
  const [originalData, setOriginalData] = useState({ 
    title: '', 
    subtitle: '', 
    description: '', 
    levels: [] 
  });
  const [pendingFields, setPendingFields] = useState({ 
    title: false, 
    subtitle: false, 
    description: false 
  });
  const [pendingLevels, setPendingLevels] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [mediaMap, setMediaMap] = useState({});
  
  const [isAddingLevel, setIsAddingLevel] = useState(false);
  const [editingLevelIndex, setEditingLevelIndex] = useState(null);
  const [modalLevelData, setModalLevelData] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(`${apiBase}/admissions/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          ...getAuthHeaders(),
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      if (!response.ok) return;
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach(m => {
          const id = m.id || m.media_id;
          const url = m.url || m.file_path || m.filepath || '';
          if (id && url) {
            map[id] = url;
            map[String(id)] = url;
            const numId = Number(id);
            if (!isNaN(numId)) map[numId] = url;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AdmissionsLevelsForm] Media error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    const str = String(mediaId).trim();
    if (!str) return null;

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:')) {
      return str;
    }

    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }

    if (mediaMap[str]) return mediaMap[str];
    if (mediaMap[mediaId]) return mediaMap[mediaId];

    const resolved = resolveMediaSrc(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${resolved}` : resolved;
      }
      return resolved;
    }

    return str;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await loadMediaMap();
      
      let section = null;
      const response = await fetch(`${apiBase}/admissions/cms/sections`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.ok && Array.isArray(result.data)) {
          section = result.data.find(s => s.section_type === 'admissions-levels');
        }
      }

      if (!section) {
        const publicResponse = await fetch(`${apiBase}/admissions/cms/sections?published=true`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            section = result.data.find(s => s.section_type === 'admissions-levels');
          }
        }
      }

      if (section) {
        setSectionId(section.id);
        let content = section.content_published || section.content || {};
        if (typeof content === 'string') {
          try { content = JSON.parse(content); } catch (e) { content = {}; }
        }
        const en = content?.en || content || {};
        
        const rawLevels = Array.isArray(en.levels) ? en.levels : [];
        const cleanLevels = rawLevels.filter(lvl => lvl && typeof lvl === 'object');

        const initialData = {
          title: en.title || 'Tailored Admissions for Every Stage',
          subtitle: en.subtitle || en.kicker || 'Admissions Pathways',
          description: en.description || '',
          levels: cleanLevels
        };

        const pMap = { title: false, subtitle: false, description: false };
        const lMap = {};

        try {
          const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pRes.ok) {
            const pResult = await pRes.json();
            if (pResult.success && Array.isArray(pResult.data?.pending_changes)) {
              pResult.data.pending_changes.forEach(c => {
                if (c.status === 'pending' && (c.locale === 'en' || !c.locale)) {
                  let val = c.new_value;
                  if (typeof val === 'string') {
                    try { val = JSON.parse(val); } catch (e) {}
                  }

                  if (['title', 'subtitle', 'description'].includes(c.field_id)) {
                    pMap[c.field_id] = true;
                    if (typeof val === 'string') {
                      initialData[c.field_id] = val;
                    }
                  } else if (c.field_id && c.field_id.includes('levels.')) {
                    const m = c.field_id.match(/levels\.(\d+)\.(add|update|delete)/);
                    if (m) {
                      const idx = parseInt(m[1], 10);
                      const op = m[2];
                      lMap[`level_${idx}`] = true;
                      if (op === 'delete') {
                        lMap[`level_${idx}_delete`] = true;
                      } else if (op === 'add') {
                        lMap[`level_${idx}_add`] = true;
                        if (val && typeof val === 'object' && !initialData.levels[idx]) {
                          initialData.levels.push(val);
                        }
                      } else if (op === 'update') {
                        lMap[`level_${idx}_update`] = true;
                        if (val && typeof val === 'object' && initialData.levels[idx]) {
                          initialData.levels[idx] = { ...initialData.levels[idx], ...val };
                        }
                      }
                    } else {
                      const mOld = c.field_id.match(/levels\.(\d+)/);
                      if (mOld) {
                        const idx = parseInt(mOld[1], 10);
                        lMap[`level_${idx}`] = true;
                      }
                    }
                  }
                }
              });
            }
          }
        } catch (pErr) {
          console.warn('[AdmissionsLevelsForm] Pending check error:', pErr.message);
        }

        setFormData(initialData);
        setOriginalData(JSON.parse(JSON.stringify(initialData)));
        setPendingFields(pMap);
        setPendingLevels(lMap);
      }
    } catch (err) {
      console.error('[AdmissionsLevelsForm] Load error:', err.message);
      toast.error('Failed to load admissions pathways and levels');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitField = async (name, label) => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (formData[name] === originalData[name]) { 
      toast.warning('No changes to submit'); 
      return; 
    }

    setSubmitting(p => ({ ...p, [name]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: label || name.replace(/_/g, ' '),
          old_value: originalData[name],
          new_value: formData[name],
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Submitted ${label || name} for Principal approval`);
        setPendingFields(p => ({ ...p, [name]: true }));
        setOriginalData(p => ({ ...p, [name]: formData[name] }));
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for ${label || name} is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [name]: false }));
    }
  };

  // Open modal to add a brand new level
  const handleOpenAddLevel = () => {
    setIsAddingLevel(true);
    setEditingLevelIndex(null);
    setModalLevelData({ 
      title: '', 
      note: '', 
      ageRange: '', 
      description: '', 
      image: '', 
      intake: '', 
      assessment: '', 
      support: '', 
      highlights: [], 
      process: '', 
      outcomes: [], 
      timeline: '' 
    });
  };

  // Open modal to edit existing level
  const handleOpenEditLevel = (idx) => {
    setIsAddingLevel(false);
    setEditingLevelIndex(idx);
    const lvl = formData.levels[idx] || {};
    setModalLevelData({ 
      ...lvl,
      highlights: Array.isArray(lvl.highlights) ? [...lvl.highlights] : (typeof lvl.highlights === 'string' ? lvl.highlights.split(/\r?\n/).filter(Boolean) : []),
      outcomes: Array.isArray(lvl.outcomes) ? [...lvl.outcomes] : (typeof lvl.outcomes === 'string' ? lvl.outcomes.split(/\r?\n/).filter(Boolean) : [])
    });
  };

  const handleCloseModal = () => {
    setIsAddingLevel(false);
    setEditingLevelIndex(null);
    setModalLevelData(null);
    setImageUploading(false);
  };

  const handleModalLevelChange = (field, value) => {
    if (!modalLevelData) return;
    setModalLevelData(prev => ({ ...prev, [field]: value }));
  };

  // Replace / Upload Pathway Photo (Only Replace, No Remove Image)
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are allowed');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const fData = new FormData();
      fData.append('file', file);
      fData.append('section_type', 'admissions-levels');

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const res = JSON.parse(xhr.responseText);
            if (res.success || res.ok) {
              const mid = res.media_id || res.id;
              const mediaPath = res.file_path || res.url || res.filepath;
              const origin = (apiBase || '').replace(/\/api\/?$/, '');
              const fullUrl = mediaPath 
                ? (mediaPath.startsWith('http') ? mediaPath : `${origin}${mediaPath}`)
                : String(mid);

              if (fullUrl) {
                handleModalLevelChange('image', fullUrl);
                if (mid) {
                  setMediaMap(prev => ({
                    ...prev,
                    [mid]: fullUrl,
                    [String(mid)]: fullUrl
                  }));
                }
                toast.success('Pathway photo uploaded successfully');
              } else {
                toast.error('No media URL returned');
              }
            } else {
              toast.error(res.error || 'Upload failed');
            }
          } catch (e) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload error - network failure');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/admissions/cms/media/upload?section_type=admissions-levels`);
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.withCredentials = true;
      xhr.send(fData);
    } catch (err) {
      toast.error('Error: ' + err.message);
      setImageUploading(false);
    }
  };

  // Propose Deletion of Level Pathway
  const handleDeleteLevel = async (idx) => {
    const targetLevel = formData.levels[idx];
    if (!targetLevel) return;

    if (!window.confirm(`Are you sure you want to propose deleting "${targetLevel.title || `Level ${idx + 1}`}"? This will require Principal approval.`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not available');
      return;
    }

    const key = `del_level_${idx}`;
    setSubmitting(p => ({ ...p, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `levels.${idx}.delete`,
          field_name: `Delete Admission Level: ${targetLevel.title || `Level ${idx + 1}`}`,
          old_value: targetLevel,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || res.ok) {
        toast.success(`Deletion proposal for "${targetLevel.title || 'Level'}" submitted for Principal approval`);
        setPendingLevels(p => ({
          ...p,
          [`level_${idx}`]: true,
          [`level_${idx}_delete`]: true
        }));
      } else {
        if (res.status === 409) {
          toast.warning('A pending proposal for this level is already awaiting approval');
        } else {
          toast.error(result.error || result.message || 'Deletion proposal failed');
        }
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Error submitting deletion: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [key]: false }));
    }
  };

  // Save Modal (Add or Update Level)
  const handleSubmitModalLevel = async () => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (!modalLevelData?.title?.trim()) { 
      toast.warning('Level title is required'); 
      return; 
    }
    if (!modalLevelData?.description?.trim()) {
      toast.warning('Level overview description is required');
      return;
    }

    const isAdd = isAddingLevel;
    const targetIdx = isAdd ? (formData.levels?.length || 0) : editingLevelIndex;
    const submitKey = isAdd ? 'add_level' : `level_${targetIdx}`;

    // Clean up highlights and outcomes as arrays
    const cleanLevel = {
      ...modalLevelData,
      highlights: Array.isArray(modalLevelData.highlights)
        ? modalLevelData.highlights.filter(Boolean)
        : (typeof modalLevelData.highlights === 'string'
            ? modalLevelData.highlights.split(/\r?\n/).map(s => s.trim().replace(/^[•\-*✓]\s*/, '')).filter(Boolean)
            : []),
      outcomes: Array.isArray(modalLevelData.outcomes)
        ? modalLevelData.outcomes.filter(Boolean)
        : (typeof modalLevelData.outcomes === 'string'
            ? modalLevelData.outcomes.split(/\r?\n/).map(s => s.trim().replace(/^[•\-*★]\s*/, '')).filter(Boolean)
            : [])
    };

    setSubmitting(p => ({ ...p, [submitKey]: true }));

    const fieldId = isAdd ? `levels.${targetIdx}.add` : `levels.${targetIdx}.update`;
    const fieldName = isAdd ? `Add Admission Level: ${cleanLevel.title}` : `Update Admission Level: ${cleanLevel.title}`;
    const oldValue = isAdd ? null : (originalData.levels[targetIdx] || {});

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: fieldName,
          old_value: oldValue,
          new_value: cleanLevel,
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success(isAdd 
          ? `Admission level "${cleanLevel.title}" proposed for Principal approval` 
          : `Level "${cleanLevel.title}" proposed for Principal approval`);

        setPendingLevels(p => ({ 
          ...p, 
          [`level_${targetIdx}`]: true,
          [isAdd ? `level_${targetIdx}_add` : `level_${targetIdx}_update`]: true
        }));

        if (isAdd) {
          setFormData(p => ({
            ...p,
            levels: [...p.levels, cleanLevel]
          }));
        } else {
          setFormData(p => ({
            ...p,
            levels: p.levels.map((l, i) => i === targetIdx ? cleanLevel : l)
          }));
        }

        handleCloseModal();
      } else {
        if (res.status === 409) {
          toast.warning('A pending proposal for this level is already awaiting approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [submitKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading admissions pathways and levels...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Layers size={22} />
        </div>
        <div>
          <h2 className="unified-title">Admissions Pathways & Levels</h2>
          <p className="unified-subtitle">
            Manage stage descriptions, age ranges, intakes, pathway photos, and milestone assessments
          </p>
        </div>
      </div>

      {/* Section Subtitle Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Tag / Subtitle</label>
            <p className="unified-field-desc">Small colored kicker above the section title (e.g. Admissions Pathways)</p>
          </div>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={submitting.subtitle || pendingFields.subtitle}
            placeholder="e.g. Admissions Pathways"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle', 'Section Tag / Subtitle')}
            disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} />
            {submitting.subtitle ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Title Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Main Section Title</label>
            <p className="unified-field-desc">Headline introducing all admission stage pathways</p>
          </div>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Tailored Admissions for Every Stage"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', 'Main Section Title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} />
            {submitting.title ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Description Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Overview Description</label>
            <p className="unified-field-desc">Descriptive introduction summarizing the overall admissions process across levels</p>
          </div>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="e.g. Discover admissions programs designed for Preschool through Secondary..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('description', 'Section Overview Description')}
            disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
          >
            <Save size={14} />
            {submitting.description ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Level Pathways Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Educational Level Pathways ({formData.levels.length})</label>
            <p className="unified-field-desc">Individual pathway cards featuring photos, age groups, and intake criteria</p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleOpenAddLevel}
          >
            <Plus size={14} /> Add Level
          </button>
        </div>

        {formData.levels.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#64748b' }}>
            <Layers size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No level pathways configured</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "Add Level" above to create an admissions pathway</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
            {formData.levels.map((level, idx) => {
              const key = `level_${idx}`;
              const isPending = pendingLevels[key];
              const isPendingDelete = pendingLevels[`level_${idx}_delete`];
              const isPendingAdd = pendingLevels[`level_${idx}_add`];
              const isDeleting = submitting[`del_level_${idx}`];
              const resolvedUrl = getMediaUrl(level.image);

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '14px 18px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.2s ease',
                    opacity: isDeleting ? 0.6 : 1
                  }}
                >
                  {/* Thumbnail */}
                  <div
                    style={{
                      width: '130px',
                      height: '84px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      background: '#e2e8f0',
                      flexShrink: 0,
                      position: 'relative'
                    }}
                  >
                    {resolvedUrl ? (
                      <img
                        src={resolvedUrl}
                        alt={level.title || `Level ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94a3b8'
                        }}
                      >
                        <ImageIcon size={22} />
                      </div>
                    )}
                    {resolvedUrl && (
                      <button
                        type="button"
                        onClick={() => setLightboxImage(resolvedUrl)}
                        style={{
                          position: 'absolute',
                          bottom: '4px',
                          right: '4px',
                          background: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                          border: 'none',
                          padding: '3px 5px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="View Full Photo"
                      >
                        <Eye size={11} />
                      </button>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                      <h4 style={{ margin: 0, fontSize: '15.5px', fontWeight: 800, color: '#0f172a' }}>
                        {level.title || `Level ${idx + 1}`}
                      </h4>
                      {level.note && (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: '#fef3c7',
                            color: '#92400e',
                            fontSize: '11px',
                            fontWeight: 700
                          }}
                        >
                          {level.note}
                        </span>
                      )}
                      {level.ageRange && (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: '#e0e7ff',
                            color: '#4338ca',
                            fontSize: '11px',
                            fontWeight: 600
                          }}
                        >
                          {level.ageRange}
                        </span>
                      )}
                      {isPendingDelete && (
                        <span 
                          className="unified-pending-pill" 
                          style={{ 
                            padding: '2px 8px', 
                            fontSize: '10px', 
                            background: '#fee2e2', 
                            color: '#991b1b', 
                            border: '1px solid #fecaca' 
                          }}
                        >
                          <Clock size={10} /> Pending Deletion
                        </span>
                      )}
                      {!isPendingDelete && isPendingAdd && (
                        <span 
                          className="unified-pending-pill" 
                          style={{ 
                            padding: '2px 8px', 
                            fontSize: '10px', 
                            background: '#ecfdf5', 
                            color: '#065f46', 
                            border: '1px solid #a7f3d0' 
                          }}
                        >
                          <Clock size={10} /> Pending Addition
                        </span>
                      )}
                      {!isPendingDelete && !isPendingAdd && isPending && (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Review
                        </span>
                      )}
                    </div>

                    <p
                      style={{
                        margin: '0 0 6px',
                        fontSize: '13px',
                        color: '#475569',
                        lineHeight: 1.5,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {level.description || 'No description entered yet.'}
                    </p>

                    {level.intake && (
                      <p style={{ margin: 0, fontSize: '11.5px', color: '#64748b' }}>
                        <span style={{ fontWeight: 600 }}>Intake:</span> {level.intake}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={() => handleOpenEditLevel(idx)}
                      disabled={isPending || isDeleting}
                    >
                      <Edit3 size={14} /> Edit Level
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteLevel(idx)}
                      disabled={isPending || isDeleting}
                      style={{
                        padding: '8px 12px',
                        color: isPending || isDeleting ? '#94a3b8' : '#ef4444',
                        background: isPending || isDeleting ? '#f1f5f9' : '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        cursor: isPending || isDeleting ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Propose Deleting Level"
                    >
                      {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit / Add Level Modal */}
      {(isAddingLevel || editingLevelIndex !== null) && modalLevelData && (
        <div
          className="unified-modal-overlay"
          onClick={handleCloseModal}
        >
          <div className="unified-modal" style={{ maxWidth: '720px' }} onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
                  {isAddingLevel ? 'Add New Admissions Level' : `Edit Pathway: ${modalLevelData.title || ''}`}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Configure title, age qualifications, pathway photo, and intake timeline
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Photo Preview & Replace Photo (Only Replace, No Remove Image) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0'
                }}
              >
                <div
                  style={{
                    width: '160px',
                    height: '100px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#e2e8f0',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #cbd5e1'
                  }}
                >
                  {modalLevelData.image && getMediaUrl(modalLevelData.image) ? (
                    <img
                      src={getMediaUrl(modalLevelData.image)}
                      alt="Level preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                      <ImageIcon size={28} style={{ margin: '0 auto 4px', opacity: 0.5 }} />
                      <span style={{ fontSize: '11px', display: 'block' }}>No image</span>
                    </div>
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <input
                    type="file"
                    id="level-modal-file-input"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor="level-modal-file-input"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '9px 16px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      cursor: imageUploading ? 'not-allowed' : 'pointer',
                      color: '#2563eb',
                      fontWeight: 600,
                      fontSize: '13px'
                    }}
                  >
                    <Upload size={14} />
                    {imageUploading
                      ? `Uploading (${imageProgress}%)...`
                      : (modalLevelData.image ? 'Replace Pathway Photo' : 'Upload Pathway Photo')}
                  </label>
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#64748b' }}>
                    Recommended: 600×250px or 16:9 • JPG, PNG, WebP (Max 5MB)
                  </p>
                </div>
              </div>

              {/* Title & Badge */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Level Title *
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalLevelData.title || ''}
                    onChange={(e) => handleModalLevelChange('title', e.target.value)}
                    placeholder="e.g. Preschool & Kindergarten Admissions"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Note / Badge
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalLevelData.note || ''}
                    onChange={(e) => handleModalLevelChange('note', e.target.value)}
                    placeholder="e.g. Trial class available"
                  />
                </div>
              </div>

              {/* Age Range & Timeline */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Age Range
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalLevelData.ageRange || ''}
                    onChange={(e) => handleModalLevelChange('ageRange', e.target.value)}
                    placeholder="e.g. Ages 3-6 or Grades 1-6"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Estimated Timeline
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalLevelData.timeline || ''}
                    onChange={(e) => handleModalLevelChange('timeline', e.target.value)}
                    placeholder="e.g. 2-3 weeks"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Overview Description *
                </label>
                <textarea
                  className="unified-textarea"
                  rows={3}
                  value={modalLevelData.description || ''}
                  onChange={(e) => handleModalLevelChange('description', e.target.value)}
                  placeholder="Summary of this educational pathway..."
                />
              </div>

              {/* Intake & Assessment */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Intake Details
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalLevelData.intake || ''}
                    onChange={(e) => handleModalLevelChange('intake', e.target.value)}
                    placeholder="e.g. Rolling intake Jan - Jul"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Assessment Method
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalLevelData.assessment || ''}
                    onChange={(e) => handleModalLevelChange('assessment', e.target.value)}
                    placeholder="e.g. Play-based observation / Written tests"
                  />
                </div>
              </div>

              {/* Process & Support */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Process Summary (use →)
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalLevelData.process || ''}
                    onChange={(e) => handleModalLevelChange('process', e.target.value)}
                    placeholder="e.g. Inquiry → Assessment → Acceptance"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Student & Family Support
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalLevelData.support || ''}
                    onChange={(e) => handleModalLevelChange('support', e.target.value)}
                    placeholder="e.g. Orientation, transition plan..."
                  />
                </div>
              </div>

              {/* Key Checkpoints & Expected Outcomes */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Key Checkpoints (one per line)
                  </label>
                  <textarea
                    className="unified-textarea"
                    rows={3}
                    value={Array.isArray(modalLevelData.highlights) ? modalLevelData.highlights.join('\n') : (modalLevelData.highlights || '')}
                    onChange={(e) => handleModalLevelChange('highlights', e.target.value)}
                    placeholder="Online application submission&#10;Placement observation&#10;Parent dialogue"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Expected Outcomes (one per line)
                  </label>
                  <textarea
                    className="unified-textarea"
                    rows={3}
                    value={Array.isArray(modalLevelData.outcomes) ? modalLevelData.outcomes.join('\n') : (modalLevelData.outcomes || '')}
                    onChange={(e) => handleModalLevelChange('outcomes', e.target.value)}
                    placeholder="Seamless transition plan&#10;Individualized learning profile"
                  />
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                disabled={submitting[isAddingLevel ? 'add_level' : `level_${editingLevelIndex}`] || !modalLevelData.title?.trim() || !modalLevelData.description?.trim()}
                onClick={handleSubmitModalLevel}
              >
                <Save size={14} />
                {submitting[isAddingLevel ? 'add_level' : `level_${editingLevelIndex}`]
                  ? 'Submitting...'
                  : (isAddingLevel ? 'Propose Add Level' : 'Save & Submit Level')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: '#000',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              <X size={18} />
            </button>
            <img
              src={lightboxImage}
              alt="Full Preview"
              style={{ maxWidth: '100%', maxHeight: '85vh', display: 'block', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
